import { useState, useEffect, useRef, useMemo } from "react";
import { Grid, Box, CardContent, Typography, Alert, TextField, Paper, Button } from "@mui/material";
import { IAPISParty, IPartialAPISParty } from "@vmosolutions/oc-interfaces";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { sortUsersByLastName } from "../Users/ManageUsers";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { convertDateObjectToString } from "src/utils";
import { APISForm } from "./APISForm";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";

interface IManageApisProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  height: number;
}

/**
 * Manage User related collections
 * @param props -
 */
export const ManageAPIS = (props: IManageApisProps): JSX.Element => {
  const { setActiveModal, height } = props;
  const { cancellablePromise } = useCancellablePromise();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedParty, setSelectedParty] = useState<IAPISParty | null>();
  const [APISParties, setAPISParties] = useState<IPartialAPISParty[]>([]);
  const [APISPartyFilter, setAPISPartyFilter] = useState<string>("");

  useEffect(() => {
    const getAPISPartiesAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IPartialAPISParty[]>(
        "/APIS/parties?omitMarkedForDeletion=true",
        {},
        cancellablePromise
      );
      setIsLoading(false);
      if (Array.isArray(response)) setAPISParties(response.sort(sortUsersByLastName));
    };
    getAPISPartiesAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAPISPartyList = (updatedParty: IAPISParty, mode: "create" | "update" | "delete"): void => {
    const newAPISParties = [...APISParties];
    const index = APISParties.findIndex((party: IPartialAPISParty) => party._id === updatedParty._id);
    switch (true) {
      case updatedParty._id && mode === "create": // User Created
        newAPISParties.push(updatedParty);
        setSelectedParty(updatedParty);
        break;
      case updatedParty._id && mode === "update" && index > -1: // User Updated
        newAPISParties[index] = updatedParty;
        setSelectedParty(updatedParty);
        break;
      case updatedParty._id && mode === "delete" && index > -1: // User Deleted
        newAPISParties.splice(index, 1);
        setSelectedParty(null);
        break;
      default:
    }
    newAPISParties.sort(sortUsersByLastName);
    setAPISParties(newAPISParties);
  };

  const handleRetrieveAPISParty = (party: IPartialAPISParty): void => {
    if (party?._id) {
      const getPartyAction = async (): Promise<void> => {
        const fullParty = await AmplifyAPIWrapper.Get<IAPISParty>(`/APIS/${party._id}`);
        setSelectedParty(fullParty);
      };
      getPartyAction();
    } else {
      setSelectedParty(null);
    }
  };

  return (
    <CardContent>
      {isLoading && <LoadingWrapper message="loading APIS data" />}

      <Grid container item spacing={3}>
        <Grid container item xs={3}>
          <Paper elevation={4} sx={{ pl: 1, pb: 1, width: "100%" }}>
            <Grid item xs={12}>
              <TextField
                placeholder="Filter Names"
                variant="outlined"
                size="small"
                fullWidth
                onChange={(e) => {
                  const val = e.target.value;
                  setAPISPartyFilter(val);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                color="success"
                variant="contained"
                size="small"
                onClick={() => setSelectedParty(null)}
                sx={{ cursor: "pointer" }}
              >
                <AddIcon fontSize="small" /> Add APIS Party
              </Button>
            </Grid>
            <Grid container item xs={12}>
              <Grid item xs={12} sx={{ height: `calc(${height}px - 120px)`, overflowY: "scroll" }}>
                {APISParties.filter((party) => {
                  const filter = APISPartyFilter.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");
                  return (
                    party.firstName.toLowerCase().includes(filter) || party.lastName.toLowerCase().includes(filter)
                  );
                }).map((item, key) => {
                  return (
                    <DraggableListItem
                      active={item._id === selectedParty?._id ? true : false}
                      key={key}
                      sx={{ cursor: "pointer", alignItems: "center", display: "flex" }}
                      onClick={() => handleRetrieveAPISParty(item)}
                    >
                      {item.user_id ? <FolderSharedIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                      <Box sx={{ pl: 1, width: "100%" }}>
                        {item.lastName}, {item.firstName}
                      </Box>
                    </DraggableListItem>
                  );
                })}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={9}>
          <Grid container>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "start", minHeight: 50 }}>
              <Typography variant="h6">
                Manage APIS
                {selectedParty && (
                  <>
                    {" "}
                    ({`${selectedParty.firstName} ${selectedParty.lastName}`})
                    {selectedParty.updatePending ? (
                      <Box sx={{ fontSize: "12px", color: "warning.light" }}>(Update Pending)</Box>
                    ) : (
                      ""
                    )}
                  </>
                )}
              </Typography>
            </Grid>
            <Grid item xs={"auto"}>
              {!!selectedParty && !!selectedParty?.lastMCLUpdate && (
                <Alert severity="info" variant="filled">
                  {`last MCL update sent at: ${convertDateObjectToString(new Date(selectedParty.lastMCLUpdate))} UTC`}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sx={{ pt: 3, height: `calc(${height}px - 100px)`, overflowY: "scroll" }}>
              <APISForm
                selectedParty={selectedParty}
                APISParties={APISParties}
                updateAPISList={updateAPISPartyList}
                setActiveModal={setActiveModal}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};
